import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Button, Card, CardContent, CircularProgress, 
  Container, Divider, FormControl, Grid, InputLabel, 
  MenuItem, Paper, Select, Slider, Snackbar, TextField, 
  Typography, Alert, Stack, Chip, Zoom, Fade, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, Tooltip, Avatar, useTheme, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Skeleton,
  useMediaQuery, StepContent, Switch, FormControlLabel
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DownloadIcon from '@mui/icons-material/Download';
import FoodBankIcon from '@mui/icons-material/FoodBank';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SportsBarIcon from '@mui/icons-material/SportsBar';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import BreakfastDiningIcon from '@mui/icons-material/BreakfastDining';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import MessageIcon from '@mui/icons-material/Message';
import IosShareIcon from '@mui/icons-material/IosShare';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { generateMealPlan } from '../services/openRouterService';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';
import SupabaseService from '../supabase/supabaseService';

const MealPlanner = () => {
  const { user } = useSupabaseAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const plannerRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  
  // Form state
  const [days, setDays] = useState(7);
  const [dietary, setDietary] = useState('');
  const [nutritionalFocus, setNutritionalFocus] = useState('balanced');
  const [caloriesPerDay, setCaloriesPerDay] = useState(2000);
  const [includeSnacks, setIncludeSnacks] = useState(true);
  const [preferLocalIngredients, setPreferLocalIngredients] = useState(false);
  const [specialOccasion, setSpecialOccasion] = useState('');
  
  // Processing state
  const [loading, setLoading] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [mealPlan, setMealPlan] = useState(null);
  const [parsedPlan, setParsedPlan] = useState(null);
  const [savedPlanId, setSavedPlanId] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  
  // UI state
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [tipDialogOpen, setTipDialogOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Psychology-informed color palette
  const colors = {
    primary: '#4a148c', // Deep purple - represents creativity, wisdom
    secondary: '#ff9800', // Orange - stimulates appetite, energy
    accent: '#2e7d32', // Green - health, freshness
    background: '#f5f3f7', // Subtle lavender - calming, promotes mindfulness
    breakfast: '#1976d2', // Blue - morning, fresh start
    lunch: '#ff9800',    // Orange - midday energy
    dinner: '#6a1b9a',   // Purple - evening relaxation
    snack: '#43a047'     // Green - healthy choices
  };

  // Processing steps for engaging feedback
  const processingSteps = [
    "Analyzing your dietary preferences...",
    "Creating balanced nutritional profiles...",
    "Designing your personalized meal calendar...",
    "Finalizing your meal plan..."
  ];

  // Effect to advance processing steps during loading
  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setProcessingStep(prev => (prev < processingSteps.length - 1 ? prev + 1 : prev));
      }, 3000);
    } else {
      setProcessingStep(0);
    }
    
    return () => clearInterval(interval);
  }, [loading]);

  // Parse meal plan text into structured data
  useEffect(() => {
    if (mealPlan) {
      parseMealPlanText(mealPlan);
    }
  }, [mealPlan]);

  const parseMealPlanText = (mealPlanText) => {
    try {
      // First sanitize to remove markdown symbols
      const sanitizedText = sanitizeMealPlanText(mealPlanText);
      
      // Initialize meal plan structure
      const planData = {
        days: []
      };
      
      // Extract days and meals using regex
      const daysRegex = /Day\s+(\d+)[:\s]*(.*?)(?=Day\s+\d+|$)/gis;
      let dayMatch;
      
      while ((dayMatch = daysRegex.exec(sanitizedText)) !== null) {
        const dayNumber = parseInt(dayMatch[1]);
        const dayContent = dayMatch[2].trim();
        
        const day = {
          number: dayNumber,
          breakfast: extractMeal(dayContent, 'breakfast'),
          lunch: extractMeal(dayContent, 'lunch'),
          dinner: extractMeal(dayContent, 'dinner'),
          snacks: extractMeal(dayContent, 'snack'),
          nutritionalSummary: extractNutritionalSummary(dayContent)
        };
        
        planData.days.push(day);
      }
      
      // Sort days by number
      planData.days.sort((a, b) => a.number - b.number);
      
      setParsedPlan(planData);
    } catch (error) {
      console.error('Error parsing meal plan:', error);
      // Set a basic empty structure
      setParsedPlan({
        days: []
      });
    }
  };

  const extractMeal = (dayContent, mealType) => {
    const mealRegex = new RegExp(`${mealType}[:\\s]*(.*?)(?=lunch|dinner|breakfast|snack|nutritional|$)`, 'is');
    const match = dayContent.match(mealRegex);
    
    if (!match) return { title: 'Not specified', description: '', ingredients: [] };
    
    const mealContent = match[1].trim();
    
    // Try to extract title, description and ingredients
    let title = 'Not specified';
    let description = '';
    let ingredients = [];
    
    // Extract title (usually the first line)
    const titleMatch = mealContent.match(/^(.*?)(?:\n|$)/);
    if (titleMatch) title = titleMatch[1].trim();
    
    // Extract description (second paragraph usually)
    const descMatch = mealContent.match(/^.*?\n(.*?)(?:\n|$)/s);
    if (descMatch) description = descMatch[1].trim();
    
    // Extract ingredients (usually appear after "ingredients" label or as a list)
    const ingredientsMatch = mealContent.match(/ingredients[:\s]*(.*?)(?=\n\n|$)/is);
    if (ingredientsMatch) {
      ingredients = ingredientsMatch[1]
        .split(/[,\n•]/)
        .map(item => item.trim())
        .filter(item => item.length > 0);
    } else {
      // Try to extract from bullet points or lists
      const listItems = mealContent.match(/[•\-\*]\s*(.*?)(?=\n|$)/g);
      if (listItems) {
        ingredients = listItems.map(item => item.replace(/[•\-\*]\s*/, '').trim());
      }
    }
    
    // If we couldn't extract specific ingredients, just take content after first line
    if (ingredients.length === 0) {
      ingredients = mealContent
        .split('\n')
        .slice(1)
        .map(line => line.trim())
        .filter(line => line.length > 0);
    }
    
    return {
      title,
      description,
      ingredients
    };
  };

  const extractNutritionalSummary = (dayContent) => {
    const nutritionalRegex = /nutritional\s+summary[:\s]*(.*?)(?=\n\n|$)/is;
    const match = dayContent.match(nutritionalRegex);
    
    if (!match) return [];
    
    return match[1]
      .split(/\n|[•\-\*]/)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  const sanitizeMealPlanText = (text) => {
    if (!text) return '';
    
    // Remove markdown headings (# Heading)
    let sanitized = text.replace(/^#+\s+(.*)$/gm, '$1');
    
    // Remove bold/italic markdown (*text* or **text**)
    sanitized = sanitized.replace(/(\*\*|\*)(.*?)(\*\*|\*)/g, '$2');
    
    // Replace bullet points with clean bullets
    sanitized = sanitized.replace(/^\s*[\*\-]\s+(.*)$/gm, '• $1');
    
    // Replace markdown links [text](url) with just text
    sanitized = sanitized.replace(/\[(.*?)\]\(.*?\)/g, '$1');
    
    // Remove any remaining markdown formatting characters
    sanitized = sanitized.replace(/[_~`]/g, '');
    
    return sanitized;
  };

  const handleGenerateMealPlan = async () => {
    try {
      setLoading(true);
      setMealPlan(null);
      setParsedPlan(null);
      setIsSaved(false);
      setSavedPlanId(null);
      
      // Create parameters object with all preferences
      const params = {
        days,
        dietary,
        nutritionalGoals: {
          focus: nutritionalFocus,
          calories: caloriesPerDay
        },
        includeSnacks,
        preferLocalIngredients,
        specialOccasion
      };
      
      // Generate meal plan
      const generatedPlan = await generateMealPlan(params);
      
      // Set the raw meal plan text
      setMealPlan(generatedPlan);
      
      // Success notification
      toast.success('Meal plan created successfully!', {
        icon: <AutoAwesomeIcon style={{ color: colors.secondary }} />
      });
      
      // Automatically save for logged in users
      if (user) {
        try {
          const savedPlan = await SupabaseService.addRecord('meal_plans', {
            days,
            dietary,
            nutritional_focus: nutritionalFocus,
            calories_per_day: caloriesPerDay,
            include_snacks: includeSnacks,
            prefer_local_ingredients: preferLocalIngredients,
            special_occasion: specialOccasion,
            meal_plan_text: generatedPlan,
            is_saved: true
          });
          
          setSavedPlanId(savedPlan.id);
          setIsSaved(true);
        } catch (saveError) {
          console.error('Error saving meal plan:', saveError);
        }
      }
      
    } catch (error) {
      console.error('Error generating meal plan:', error);
      toast.error('Failed to generate meal plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSavePlan = async () => {
    if (!user) {
      toast.info('Please log in to save meal plans');
      return;
    }

    try {
      if (isSaved && savedPlanId) {
        // Update the saved status
        await SupabaseService.updateRecord('meal_plans', savedPlanId, {
          is_saved: false
        });
        
        setIsSaved(false);
        toast.success('Meal plan removed from saved collection');
      } else {
        // Save plan if not already saved
        if (savedPlanId) {
          await SupabaseService.updateRecord('meal_plans', savedPlanId, {
            is_saved: true
          });
        } else {
          const savedPlan = await SupabaseService.addRecord('meal_plans', {
            days,
            dietary,
            nutritional_focus: nutritionalFocus,
            calories_per_day: caloriesPerDay,
            include_snacks: includeSnacks,
            prefer_local_ingredients: preferLocalIngredients,
            special_occasion: specialOccasion,
            meal_plan_text: mealPlan,
            is_saved: true
          });
          
          setSavedPlanId(savedPlan.id);
        }
        
        setIsSaved(true);
        toast.success('Meal plan saved to your collection');
      }
    } catch (error) {
      console.error('Error toggling save status:', error);
    }
  };

  const downloadPDF = async () => {
    if (!mealPlan || !parsedPlan) return;
    
    try {
      toast.info('Preparing your meal plan PDF...');
      
      if (plannerRef.current) {
        // Create a PDF document
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const contentWidth = pageWidth - (margin * 2);
        
        // Add header with styling
        doc.setFillColor(colors.primary);
        doc.rect(0, 0, pageWidth, 32, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('Meal Maestro', margin, 20);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'italic');
        doc.text(`${days}-Day Meal Plan`, pageWidth - margin, 20, { align: 'right' });
        
        // Add date and preferences
        let yPos = 45;
        
        doc.setTextColor(33, 33, 33);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Your ${days}-Day Personalized Meal Plan`, pageWidth / 2, yPos, { align: 'center' });
        
        yPos += 10;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text(`Generated on ${dayjs().format('MMMM D, YYYY')}`, pageWidth / 2, yPos, { align: 'center' });
        
        // Add plan preferences
        yPos += 15;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.primary);
        doc.text('Meal Plan Preferences', margin, yPos);
        
        doc.setLineWidth(0.5);
        doc.setDrawColor(colors.primary);
        doc.line(margin, yPos + 1, margin + 50, yPos + 1);
        
        // Create preferences summary
        yPos += 10;
        doc.setFontSize(10);
        doc.setTextColor(33, 33, 33);
        doc.setFont('helvetica', 'normal');
        
        const preferences = [
          `Dietary Focus: ${dietary || 'None specified'}`,
          `Nutritional Focus: ${nutritionalFocus}`,
          `Calories per Day: ${caloriesPerDay}`,
          `Special Requirements: ${specialOccasion || 'None'}`
        ];
        
        preferences.forEach((pref, i) => {
          doc.text(pref, margin, yPos + (i * 7));
        });
        
        // Loop through each day
        yPos += 40;
        
        parsedPlan.days.forEach((day, index) => {
          if (yPos > pageHeight - 50) {
            doc.addPage();
            yPos = margin + 10;
          }
          
          // Day header with background
          doc.setFillColor(colors.primary);
          doc.rect(margin, yPos - 8, contentWidth, 12, 'F');
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(`Day ${day.number}`, margin + 5, yPos);
          
          yPos += 15;
          
          // Add meals in a structured format
          const meals = [
            { title: 'Breakfast', data: day.breakfast, color: colors.breakfast },
            { title: 'Lunch', data: day.lunch, color: colors.lunch },
            { title: 'Dinner', data: day.dinner, color: colors.dinner }
          ];
          
          if (includeSnacks) {
            meals.push({ title: 'Snacks', data: day.snacks, color: colors.snack });
          }
          
          meals.forEach(meal => {
            if (yPos > pageHeight - 70) {
              doc.addPage();
              yPos = margin + 10;
            }
            
            // Meal title with color
            doc.setFillColor(meal.color);
            doc.roundedRect(margin, yPos - 5, 40, 8, 2, 2, 'F');
            
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(meal.title, margin + 20, yPos, { align: 'center' });
            
            // Meal content
            yPos += 10;
            doc.setTextColor(33, 33, 33);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text(meal.data.title, margin, yPos);
            
            yPos += 7;
            if (meal.data.description) {
              doc.setFontSize(9);
              doc.setFont('helvetica', 'italic');
              const descLines = doc.splitTextToSize(meal.data.description, contentWidth);
              doc.text(descLines, margin, yPos);
              yPos += (descLines.length * 5) + 2;
            }
            
            // Ingredients
            if (meal.data.ingredients && meal.data.ingredients.length > 0) {
              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              
              meal.data.ingredients.forEach(ingredient => {
                const ingLines = doc.splitTextToSize(`• ${ingredient}`, contentWidth - 5);
                doc.text(ingLines, margin, yPos);
                yPos += (ingLines.length * 5) + 1;
              });
            }
            
            yPos += 8;
          });
          
          // Add nutritional summary for the day
          if (day.nutritionalSummary && day.nutritionalSummary.length > 0) {
            if (yPos > pageHeight - 50) {
              doc.addPage();
              yPos = margin + 10;
            }
            
            doc.setFillColor(240, 240, 240);
            doc.rect(margin, yPos - 5, contentWidth, 6 + (day.nutritionalSummary.length * 5), 'F');
            
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(colors.primary);
            doc.text('Nutritional Summary', margin + 5, yPos);
            
            yPos += 7;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(33, 33, 33);
            
            day.nutritionalSummary.forEach(item => {
              doc.text(`• ${item}`, margin + 5, yPos);
              yPos += 5;
            });
            
            yPos += 15;
          } else {
            yPos += 10;
          }
          
          // Add page break between days
          if (index < parsedPlan.days.length - 1) {
            doc.addPage();
            yPos = margin + 10;
          }
        });
        
        // Add tip page at the end
        doc.addPage();
        yPos = margin + 15;
        
        doc.setFillColor(colors.primary);
        doc.rect(0, 0, pageWidth, 32, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Tips for Success', pageWidth / 2, 20, { align: 'center' });
        
        // Tips content
        const tips = [
          {
            title: 'Meal Prep Strategies',
            content: 'Dedicate 2-3 hours on weekends to prep ingredients for the week. Chop vegetables, cook grains, and prepare proteins that can be easily assembled into meals.'
          },
          {
            title: 'Storage Solutions',
            content: 'Invest in quality glass containers that can go from refrigerator to microwave. Label containers with contents and date to stay organized.'
          },
          {
            title: 'Balancing Nutrition',
            content: 'Aim for meals that contain protein, complex carbohydrates, healthy fats, and plenty of vegetables to ensure balanced nutrition.'
          },
          {
            title: 'Staying Flexible',
            content: 'Don\'t be afraid to swap ingredients based on what\'s available or in season. Many recipes can be adapted without affecting nutritional quality.'
          },
          {
            title: 'Mindful Eating',
            content: 'Take time to enjoy your meals without distractions. This promotes better digestion and satisfaction from your food.'
          }
        ];
        
        tips.forEach((tip, index) => {
          doc.setFillColor(colors.secondary + '20'); // Light shade of secondary color
          doc.roundedRect(margin, yPos, contentWidth, 30, 3, 3, 'F');
          
          doc.setTextColor(colors.primary);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(tip.title, margin + 10, yPos + 10);
          
          doc.setTextColor(33, 33, 33);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          const tipLines = doc.splitTextToSize(tip.content, contentWidth - 20);
          doc.text(tipLines, margin + 10, yPos + 18);
          
          yPos += 40;
        });
        
        // Add footer
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text('Generated by Meal Maestro - AI-Powered Meal Planning', pageWidth / 2, pageHeight - 10, { align: 'center' });
        
        // Save the PDF with formatted filename
        const fileName = `MealMaestro_${days}Day_Plan_${dayjs().format('MMDDYYYY')}.pdf`;
        doc.save(fileName);
        
        toast.success('Meal plan PDF downloaded successfully!');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const handleSharePlan = () => {
    setShareDialogOpen(true);
  };

  const closeShareDialog = () => {
    setShareDialogOpen(false);
  };

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const closeNotification = () => {
    setNotification({...notification, open: false});
  };

  const getMealIcon = (mealType) => {
    switch (mealType.toLowerCase()) {
      case 'breakfast':
        return <BreakfastDiningIcon sx={{ color: colors.breakfast }} />;
      case 'lunch':
        return <LunchDiningIcon sx={{ color: colors.lunch }} />;
      case 'dinner':
        return <DinnerDiningIcon sx={{ color: colors.dinner }} />;
      case 'snacks':
        return <SportsBarIcon sx={{ color: colors.snack }} />;
      default:
        return <FoodBankIcon />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, md: 4 }, 
          borderRadius: 2,
          background: `linear-gradient(to bottom right, ${colors.background}, white)`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: -50, 
            left: -50, 
            width: 200, 
            height: 200, 
            background: `linear-gradient(to bottom right, ${colors.primary}30, transparent)`,
            borderRadius: '50%',
            zIndex: 0
          }} 
        />
        
        <Box 
          sx={{ 
            position: 'absolute', 
            bottom: -100, 
            right: -100, 
            width: 300, 
            height: 300, 
            background: `linear-gradient(to top left, ${colors.secondary}20, transparent)`,
            borderRadius: '50%',
            zIndex: 0
          }} 
        />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
            <Grid item>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: `${colors.primary}15`,
                  color: colors.primary
                }}
              >
                <EventNoteIcon fontSize="large" />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography variant="h4" sx={{ 
                color: colors.primary, 
                fontWeight: 600,
                mb: 0.5
              }}>
                AI Meal Planner
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create a personalized meal plan tailored to your dietary needs and preferences
              </Typography>
            </Grid>
          </Grid>

          <Stepper 
            activeStep={activeStep} 
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4, py: 2, px: { xs: 0, md: 2 } }}
          >
            <Step>
              <StepLabel>Set Preferences</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      mb: 3, 
                      bgcolor: 'rgba(255,255,255,0.8)',
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="h6" gutterBottom sx={{ color: colors.primary }}>
                            Basic Settings
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Box>
                            <Typography variant="body2" gutterBottom>
                              Number of Days: {days}
                            </Typography>
                            <Slider
                              value={days}
                              onChange={(e, newValue) => setDays(newValue)}
                              step={1}
                              marks
                              min={1}
                              max={14}
                              valueLabelDisplay="auto"
                              sx={{ 
                                color: colors.primary,
                                '& .MuiSlider-thumb': {
                                  height: 24,
                                  width: 24,
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="nutritional-focus-label">Nutritional Focus</InputLabel>
                            <Select
                              labelId="nutritional-focus-label"
                              value={nutritionalFocus}
                              onChange={(e) => setNutritionalFocus(e.target.value)}
                              label="Nutritional Focus"
                            >
                              <MenuItem value="balanced">Balanced</MenuItem>
                              <MenuItem value="high-protein">High Protein</MenuItem>
                              <MenuItem value="low-carb">Low Carb</MenuItem>
                              <MenuItem value="low-fat">Low Fat</MenuItem>
                              <MenuItem value="weight-loss">Weight Loss</MenuItem>
                              <MenuItem value="weight-gain">Weight Gain</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Dietary Restrictions/Preferences"
                            placeholder="e.g., vegetarian, gluten-free, dairy-free"
                            value={dietary}
                            onChange={(e) => setDietary(e.target.value)}
                            helperText="Enter any dietary restrictions or preferences"
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="body2" gutterBottom>
                              Target Calories per Day: {caloriesPerDay}
                            </Typography>
                            <Slider
                              value={caloriesPerDay}
                              onChange={(e, newValue) => setCaloriesPerDay(newValue)}
                              step={100}
                              min={1200}
                              max={3500}
                              valueLabelDisplay="auto"
                              sx={{ color: colors.secondary }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                      
                      <Divider sx={{ my: 3 }} />
                      
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={showAdvancedOptions} 
                              onChange={() => setShowAdvancedOptions(!showAdvancedOptions)} 
                              color="primary"
                            />
                          }
                          label="Advanced Options"
                        />
                      </Box>
                      
                      <Collapse in={showAdvancedOptions}>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                          <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ color: colors.primary }}>
                              Additional Preferences
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <FormControlLabel
                              control={
                                <Switch 
                                  checked={includeSnacks} 
                                  onChange={() => setIncludeSnacks(!includeSnacks)} 
                                  color="primary"
                                />
                              }
                              label="Include Snacks"
                            />
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <FormControlLabel
                              control={
                                <Switch 
                                  checked={preferLocalIngredients} 
                                  onChange={() => setPreferLocalIngredients(!preferLocalIngredients)} 
                                  color="primary"
                                />
                              }
                              label="Prefer Seasonal & Local Ingredients"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Special Occasion/Theme (Optional)"
                              placeholder="e.g., holiday, birthday, budget-friendly"
                              value={specialOccasion}
                              onChange={(e) => setSpecialOccasion(e.target.value)}
                              helperText="Add a theme or occasion for your meal plan"
                            />
                          </Grid>
                        </Grid>
                      </Collapse>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{
                            bgcolor: colors.primary,
                            '&:hover': {
                              bgcolor: '#3b107d'
                            },
                            px: 3
                          }}
                        >
                          Next
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </StepContent>
            </Step>
            
            <Step>
              <StepLabel>Generate Plan</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      mb: 3, 
                      bgcolor: 'rgba(255,255,255,0.8)',
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography variant="h6" gutterBottom sx={{ color: colors.primary }}>
                        Summary of Your Preferences
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: `${colors.primary}15`, 
                            borderRadius: 1
                          }}>
                            <Typography variant="subtitle2">Plan Duration</Typography>
                            <Typography variant="body1">{days} days</Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: `${colors.primary}15`, 
                            borderRadius: 1
                          }}>
                            <Typography variant="subtitle2">Nutritional Focus</Typography>
                            <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                              {nutritionalFocus}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: `${colors.primary}15`, 
                            borderRadius: 1
                          }}>
                            <Typography variant="subtitle2">Dietary Restrictions</Typography>
                            <Typography variant="body1">
                              {dietary || 'None specified'}
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: `${colors.primary}15`, 
                            borderRadius: 1
                          }}>
                            <Typography variant="subtitle2">Daily Calories</Typography>
                            <Typography variant="body1">
                              {caloriesPerDay} calories per day
                            </Typography>
                          </Box>
                        </Grid>
                        
                        {showAdvancedOptions && (
                          <>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ 
                                p: 2, 
                                bgcolor: `${colors.primary}15`, 
                                borderRadius: 1
                              }}>
                                <Typography variant="subtitle2">Additional Settings</Typography>
                                <Typography variant="body2" component="div">
                                  • {includeSnacks ? 'Including snacks' : 'No snacks'}
                                </Typography>
                                <Typography variant="body2" component="div">
                                  • {preferLocalIngredients ? 'Seasonal ingredients preferred' : 'Standard ingredients'}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            {specialOccasion && (
                              <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                  p: 2, 
                                  bgcolor: `${colors.primary}15`, 
                                  borderRadius: 1
                                }}>
                                  <Typography variant="subtitle2">Special Occasion/Theme</Typography>
                                  <Typography variant="body1">{specialOccasion}</Typography>
                                </Box>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                      
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: `${colors.secondary}15`,
                        borderRadius: 1,
                        mb: 3,
                        display: 'flex'
                      }}>
                        <InfoOutlinedIcon sx={{ color: colors.secondary, mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Our AI will generate a personalized meal plan based on your preferences.
                          The process takes about 15-30 seconds to complete.
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                          variant="outlined"
                          onClick={handleBack}
                          sx={{ mr: 1 }}
                        >
                          Back
                        </Button>
                        
                        <Button
                          variant="contained"
                          onClick={() => {
                            handleGenerateMealPlan();
                            handleNext();
                          }}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                          sx={{
                            bgcolor: colors.primary,
                            '&:hover': {
                              bgcolor: '#3b107d'
                            },
                            px: 3
                          }}
                        >
                          {loading ? 'Generating...' : 'Generate Meal Plan'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </StepContent>
            </Step>
            
            <Step>
              <StepLabel>View Results</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2, mb: 3 }}>
                  {mealPlan ? (
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        mb: 3 
                      }}>
                        <Button
                          variant="outlined"
                          onClick={handleReset}
                          startIcon={<CalendarMonthIcon />}
                        >
                          Create New Plan
                        </Button>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Save Meal Plan">
                            <IconButton 
                              onClick={toggleSavePlan}
                              sx={{ 
                                bgcolor: `${colors.primary}10`,
                                color: isSaved ? colors.primary : 'text.secondary' 
                              }}
                            >
                              {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Download PDF">
                            <IconButton 
                              onClick={downloadPDF}
                              sx={{ bgcolor: `${colors.secondary}10` }}
                            >
                              <DownloadIcon color="action" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Share Plan">
                            <IconButton 
                              onClick={handleSharePlan}
                              sx={{ bgcolor: `${colors.accent}10` }}
                            >
                              <IosShareIcon color="action" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Box 
                        ref={plannerRef}
                        sx={{
                          bgcolor: 'white',
                          p: { xs: 2, md: 3 },
                          borderRadius: 2,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}
                      >
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                            Your {days}-Day Meal Plan
                          </Typography>
                          
                          <Divider sx={{ mb: 2 }} />
                          
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: `${colors.primary}08`,
                            borderRadius: 1,
                            mb: 3
                          }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Nutritional Focus</Typography>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                  {nutritionalFocus}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Dietary</Typography>
                                <Typography variant="body2">
                                  {dietary || 'Standard diet'}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Calories</Typography>
                                <Typography variant="body2">
                                  {caloriesPerDay} per day
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                        
                        {parsedPlan && parsedPlan.days.map((day) => (
                          <Fade in={true} key={day.number} timeout={500 + (day.number * 100)}>
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                mb: 3, 
                                borderRadius: 2,
                                overflow: 'hidden'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  p: 2, 
                                  bgcolor: colors.primary,
                                  color: 'white'
                                }}
                              >
                                <Typography variant="h6">Day {day.number}</Typography>
                              </Box>
                              
                              <TableContainer>
                                <Table>
                                  <TableHead sx={{ bgcolor: `${colors.primary}10` }}>
                                    <TableRow>
                                      <TableCell width="20%">Meal</TableCell>
                                      <TableCell>Details</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {['breakfast', 'lunch', 'dinner', 'snacks'].map((mealType) => {
                                      // Skip snacks if not included
                                      if (mealType === 'snacks' && !includeSnacks) return null;
                                      
                                      const meal = day[mealType];
                                      if (!meal) return null;
                                      
                                      return (
                                        <TableRow key={mealType}>
                                          <TableCell 
                                            sx={{ 
                                              verticalAlign: 'top',
                                              borderLeft: `4px solid ${
                                                mealType === 'breakfast' ? colors.breakfast : 
                                                mealType === 'lunch' ? colors.lunch : 
                                                mealType === 'dinner' ? colors.dinner : 
                                                colors.snack
                                              }`
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              {getMealIcon(mealType)}
                                              <Typography 
                                                variant="subtitle2" 
                                                sx={{ 
                                                  ml: 1, 
                                                  textTransform: 'capitalize' 
                                                }}
                                              >
                                                {mealType}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Typography 
                                              variant="subtitle1" 
                                              sx={{ 
                                                fontWeight: 500,
                                                color: mealType === 'breakfast' ? colors.breakfast : 
                                                      mealType === 'lunch' ? colors.lunch : 
                                                      mealType === 'dinner' ? colors.dinner : 
                                                      colors.snack
                                              }}
                                            >
                                              {meal.title}
                                            </Typography>
                                            
                                            {meal.description && (
                                              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                                                {meal.description}
                                              </Typography>
                                            )}
                                            
                                            {meal.ingredients && meal.ingredients.length > 0 && (
                                              <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                  Ingredients
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                  {meal.ingredients.map((ing, i) => (
                                                    <Chip 
                                                      key={i} 
                                                      label={ing} 
                                                      size="small"
                                                      sx={{ 
                                                        bgcolor: 'rgba(0,0,0,0.04)', 
                                                        fontSize: '0.75rem' 
                                                      }}
                                                    />
                                                  ))}
                                                </Box>
                                              </Box>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              
                              {day.nutritionalSummary && day.nutritionalSummary.length > 0 && (
                                <Box sx={{ p: 2, bgcolor: `${colors.accent}10` }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Daily Nutritional Summary
                                  </Typography>
                                  <Grid container spacing={1}>
                                    {day.nutritionalSummary.map((item, i) => (
                                      <Grid item xs={12} sm={6} md={4} key={i}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                          <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5, color: colors.accent }} />
                                          {item}
                                        </Typography>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              )}
                            </Card>
                          </Fade>
                        ))}
                        
                        <Box 
                          sx={{ 
                            p: 2, 
                            bgcolor: `${colors.secondary}08`,
                            borderRadius: 1,
                            mt: 2,
                            display: 'flex',
                            alignItems: 'flex-start'
                          }}
                        >
                          <MessageIcon sx={{ color: colors.secondary, mr: 1, mt: 0.5 }} />
                          
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Meal Plan Tips
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              For best results, prepare ingredients in advance and store properly. 
                              Adjust portions based on your activity level and hunger signals.
                            </Typography>
                            
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setTipDialogOpen(true)}
                              sx={{ 
                                mt: 1, 
                                color: colors.secondary,
                                fontSize: '0.8rem'
                              }}
                            >
                              View More Tips
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : loading ? (
                    <Box 
                      sx={{ 
                        p: 4,
                        textAlign: 'center',
                        bgcolor: 'white',
                        borderRadius: 2
                      }}
                    >
                      <CircularProgress size={50} sx={{ color: colors.primary, mb: 3 }} />
                      
                      <Typography variant="h6" gutterBottom>
                        {processingSteps[processingStep]}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        {processingSteps.map((_, i) => (
                          <Box 
                            key={i} 
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              mx: 0.5,
                              bgcolor: i <= processingStep ? colors.primary : '#e0e0e0',
                              transition: 'all 0.5s ease'
                            }} 
                          />
                        ))}
                      </Box>
                      
                      <Box sx={{ 
                        maxWidth: 400, 
                        margin: '0 auto', 
                        p: 2, 
                        bgcolor: `${colors.primary}10`,
                        borderRadius: 2,
                        mt: 2
                      }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          {processingStep === 0 && "I'm reviewing your dietary preferences to create a balanced plan..."}
                          {processingStep === 1 && "Calculating optimal macro and micronutrient distribution based on your goals..."}
                          {processingStep === 2 && "Creating a varied meal schedule to keep your plan interesting..."}
                          {processingStep === 3 && "Adding final touches to your personalized meal plan..."}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mt: 4 }}>
                        <Grid container spacing={1}>
                          {Array(days).fill(0).map((_, i) => (
                            <Grid item xs={12} key={i}>
                              <Skeleton 
                                variant="rounded" 
                                height={80} 
                                width="100%"
                                animation="wave"
                                sx={{ mb: 1 }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        p: { xs: 3, md: 5 }, 
                        textAlign: 'center',
                        bgcolor: 'white',
                        borderRadius: 2
                      }}
                    >
                      <FoodBankIcon 
                        sx={{ 
                          fontSize: 100, 
                          color: `${colors.primary}30`, 
                          mb: 3 
                        }} 
                      />
                      
                      <Typography variant="h6" gutterBottom>
                        Generate Your Meal Plan
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                        Click the "Generate Meal Plan" button to create a personalized plan based on your preferences.
                        Your meal plan will appear here.
                      </Typography>
                      
                      <Button
                        variant="contained"
                        onClick={() => {
                          handleBack();
                        }}
                        sx={{
                          bgcolor: colors.primary,
                          '&:hover': {
                            bgcolor: '#3b107d'
                          },
                          px: 3
                        }}
                      >
                        Go Back
                      </Button>
                    </Box>
                  )}
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </Paper>
      
      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={closeShareDialog}>
        <DialogTitle sx={{ bgcolor: colors.primary, color: 'white' }}>
          Share Your Meal Plan
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Share your meal plan with friends and family.
          </Typography>
          
          <TextField
            fullWidth
            variant="outlined"
            label="Email Address"
            sx={{ mt: 2 }}
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Or copy this link to share:
          </Typography>
          
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={`${window.location.origin}/shared-plan/${savedPlanId || 'preview'}`}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShareDialog}>Cancel</Button>
          <Button 
            variant="contained"
            sx={{ bgcolor: colors.primary }}
            onClick={closeShareDialog}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Tips Dialog */}
      <Dialog open={tipDialogOpen} onClose={() => setTipDialogOpen(false)} maxWidth="md">
        <DialogTitle sx={{ bgcolor: colors.primary, color: 'white' }}>
          Meal Planning Tips & Tricks
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.primary }}>
                Planning & Preparation
              </Typography>
              <Typography variant="body2" paragraph>
                • Schedule 2-3 hours each weekend for meal prepping
              </Typography>
              <Typography variant="body2" paragraph>
                • Chop vegetables, cook grains, and prepare proteins in advance
              </Typography>
              <Typography variant="body2" paragraph>
                • Label containers with contents and date for better organization
              </Typography>
              <Typography variant="body2" paragraph>
                • Create a shopping list organized by store sections to save time
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.secondary }}>
                Nutrition & Balance
              </Typography>
              <Typography variant="body2" paragraph>
                • Aim for meals with protein, complex carbs, and healthy fats
              </Typography>
              <Typography variant="body2" paragraph>
                • Include at least 2 vegetables in lunch and dinner
              </Typography>
              <Typography variant="body2" paragraph>
                • Drink water before meals to help with portion control
              </Typography>
              <Typography variant="body2" paragraph>
                • Don't be afraid to adjust portions based on hunger levels
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.accent }}>
                Food Storage & Safety
              </Typography>
              <Typography variant="body2" paragraph>
                • Store prepped ingredients in clear containers for visibility
              </Typography>
              <Typography variant="body2" paragraph>
                • Keep most prepared foods for 3-4 days maximum
              </Typography>
              <Typography variant="body2" paragraph>
                • Freeze extra portions for quick meals later
              </Typography>
              <Typography variant="body2" paragraph>
                • Use the "first in, first out" method to reduce food waste
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: colors.dinner }}>
                Maintaining Motivation
              </Typography>
              <Typography variant="body2" paragraph>
                • Try one new recipe each week to avoid meal fatigue
              </Typography>
              <Typography variant="body2" paragraph>
                • Build in flexibility for social events or takeout nights
              </Typography>
              <Typography variant="body2" paragraph>
                • Keep a collection of 15-minute meal options for busy days
              </Typography>
              <Typography variant="body2" paragraph>
                • Celebrate successful weeks of following your meal plan
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setTipDialogOpen(false)}
            variant="contained"
            sx={{ bgcolor: colors.primary }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={closeNotification}>
        <Alert onClose={closeNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MealPlanner;
